import { ListPlus, Play, SkipForward } from 'lucide-react';
import type { ReactElement } from 'react';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useMusicPlayerStore } from '@/lib/music-player-store';

/**
 * Surfaces when a user triggers play on a kara while something else is
 * already playing. Three branches:
 *   • Play now     — replace the queue
 *   • Play next    — insert after the current track
 *   • Add to queue — append
 *
 * Mirrors the action pattern in the user's reference player.
 */
export function PlayActionModal(): ReactElement {
  const pending = useMusicPlayerStore((s) => s.pendingPlay);
  const resolve = useMusicPlayerStore((s) => s.resolvePending);
  const dismiss = useMusicPlayerStore((s) => s.dismissPending);

  return (
    <Dialog
      open={pending !== null}
      onOpenChange={(open) => {
        if (!open) dismiss();
      }}
    >
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle className="text-base">Already playing</DialogTitle>
          <DialogDescription>
            {pending
              ? `What should happen with "${pending.track.title}"?`
              : null}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2">
          <ActionRow
            icon={Play}
            label="Play now"
            description="Replace the queue and start playing this track."
            onClick={() => resolve('now')}
            highlight
          />
          <ActionRow
            icon={SkipForward}
            label="Play next"
            description="Insert right after the current track."
            onClick={() => resolve('next')}
          />
          <ActionRow
            icon={ListPlus}
            label="Add to queue"
            description="Append at the end of the queue."
            onClick={() => resolve('queue')}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ActionRow({
  icon: Icon,
  label,
  description,
  onClick,
  highlight,
}: {
  icon: typeof Play;
  label: string;
  description: string;
  onClick: () => void;
  highlight?: boolean;
}): ReactElement {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex w-full items-start gap-3 rounded-xl border p-3 text-left transition-colors ${
        highlight
          ? 'border-primary/40 bg-primary/5 hover:bg-primary/10'
          : 'border-border/70 bg-background/40 hover:bg-card'
      }`}
    >
      <div
        className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${
          highlight
            ? 'bg-primary text-primary-foreground'
            : 'bg-muted text-muted-foreground'
        }`}
      >
        <Icon className="h-4 w-4" />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold text-foreground">{label}</p>
        <p className="text-xs text-muted-foreground">{description}</p>
      </div>
    </button>
  );
}

export default PlayActionModal;
